/**
 * Collision du brain — boîtes englobantes en espace scène.
 *
 * brain.js déléguait tout à Pixi : `checkCollision` (1867) comparait deux
 * `getBounds()`, c'est-à-dire deux AABB en coordonnées écran. Ici on calcule
 * ces AABB nous-mêmes, ce qui rend la logique pure et testable — et évite de
 * lire la mise en page du navigateur à chaque `pointermove`.
 *
 * Le calcul est exact plutôt qu'approché grâce à une propriété du modèle :
 * un sprite enfant d'un masque tourné est CONTRE-tourné pour rester droit
 * (brain.js:1530). Son orientation en espace scène est donc toujours nulle, et
 * son AABB est un simple carré. Seul son CENTRE subit la rotation du masque.
 */

import type { MaskLayout } from "./geometry"

/** AABB en px de scène, ancrée sur son coin haut-gauche. */
export type Rect = {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Deux AABB se recouvrent-elles ?
 *
 * Transcription exacte de brain.js:1867-1876, inégalités strictes comprises :
 * deux boîtes qui se touchent exactement ne comptent pas comme une collision.
 */
export function overlaps(a: Rect, b: Rect): boolean {
  return (
    a.x + a.width > b.x &&
    a.x < b.x + b.width &&
    a.y + a.height > b.y &&
    a.y < b.y + b.height
  )
}

/** Fait tourner un point autour d'un centre. Angle en radians, sens horaire. */
export function rotateAround(
  x: number,
  y: number,
  centerX: number,
  centerY: number,
  radians: number
): { x: number; y: number } {
  if (radians === 0) return { x, y }

  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  const dx = x - centerX
  const dy = y - centerY

  return {
    x: centerX + dx * cos - dy * sin,
    y: centerY + dx * sin + dy * cos,
  }
}

/**
 * Centre, en espace scène, d'un sprite enfant d'un masque.
 *
 * Les coordonnées de l'enfant sont locales : l'origine est le coin haut-gauche
 * de la boîte du masque, avant rotation. On applique donc la rotation du
 * masque autour de son centre.
 */
export function childStageCenter(
  mask: MaskLayout,
  localX: number,
  localY: number
): { x: number; y: number } {
  return rotateAround(
    mask.left + localX,
    mask.top + localY,
    mask.left + mask.width / 2,
    mask.top + mask.height / 2,
    mask.rotation
  )
}

/** AABB d'un sprite centré en (x, y), de côté `size`. */
export function centeredRect(x: number, y: number, size: number): Rect {
  return { x: x - size / 2, y: y - size / 2, width: size, height: size }
}

/**
 * AABB, en espace scène, d'un sprite enfant d'un masque (figure ou dropzone).
 *
 * `size` est passé plutôt que lu dans le layout parce qu'il est ANIMÉ : le
 * clignotement des dropzones vides les fait passer de 100 à 80
 * (brain.js:1113-1119), et cette taille compte dans la collision puisque
 * `getBounds()` la reflétait.
 */
export function childStageRect(
  mask: MaskLayout,
  localX: number,
  localY: number,
  size: number
): Rect {
  const center = childStageCenter(mask, localX, localY)
  return centeredRect(center.x, center.y, size)
}
